import React, { Component } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Container } from 'bloomer/lib/layout/Container';
import { Box } from 'bloomer/lib/elements/Box';
import { Title } from 'bloomer/lib/elements/Title';
import api from '../util/api';
import Animal from '../Entities/Animal';
import LoadingSpinner from '../elements/LoadingSpinner';
import { Button } from 'bloomer/lib/elements/Button';
import moment, { Moment } from 'moment';
import { Subtitle } from 'bloomer/lib/elements/Subtitle';
import { Section } from 'bloomer/lib/layout/Section';
import ReactTable from 'react-table';
import Modal from '../components/Modal';
import DateRangeForm from '../components/SelfContainerDateRangeForm';
import { popupErrorFromServer } from '../util/helperFunctions';
interface PropTypes extends RouteComponentProps<{ aNumber: string }> {}

interface StateTypes {
  animal: Animal | null;
  isLoading: boolean;
  isModalOpen: boolean;
  modalEvals: Evaluation[];
  startDate: Moment;
  endDate: Moment;
}

class EvaluationSingleAnimalStatistics extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    animal: null,
    isLoading: true,
    isModalOpen: false,
    modalEvals: [],
    startDate: moment().startOf('week'),
    endDate: moment().endOf('week'),
  };

  async refresh() {
    try {
      const { startDate, endDate } = this.state;
      const { aNumber } = this.props;

      this.setState({ isLoading: true });

      if (!aNumber) return;
      const animalProm = api.animals.getOne(aNumber);
      const evaluationProm = api.evaluations.get({
        aNumber,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      const [animal, evaluations] = await Promise.all([animalProm, evaluationProm]);

      animal.findAndRegisterEvaluations(evaluations);

      this.setState({
        animal,
        isLoading: false,
      });
    } catch (err) {
      popupErrorFromServer(err);
    }
  }

  componentDidMount() {
    this.refresh();
  }

  openModal = (evals: Evaluation[]) => () =>
    this.setState({
      isModalOpen: true,
      modalEvals: evals,
    });

  closeModal = () =>
    this.setState({
      isModalOpen: false,
      modalEvals: [],
    });

  getColumns = (animal: Animal) => {
    const { startDate, endDate } = this.state;
    const numberOfDays = Math.round(endDate.diff(startDate, 'days', true));
    console.log(numberOfDays);
    const week = Array(numberOfDays)
      .fill(startDate.clone())
      .map((day: Moment, i) =>
        day
          .clone()
          .startOf('week')
          .add(i, 'days'),
      );

    const weekColumns = week.map(day => {
      const getEvalsForTime = (time: Evaluation['time']) =>
        animal.evaluations.filter(evl => day.isSame(evl.timestamp, 'day') && evl.time === time);

      const createColumnAccessor = (time: Evaluation['time']) => (keyname: any) => {
        const evaluation = getEvalsForTime(time)[0];

        if (evaluation && evaluation[keyname as keyof Evaluation]) {
          return evaluation[keyname as keyof Evaluation];
        }
        return ' - ';
      };

      const makeTimeColumn = (time: Evaluation['time']) => ({
        Header: time,
        id: `${animal.aNumber}-${day.unix()}-${time}`,
        className: `column-${time}`,
        accessor: createColumnAccessor(time),
        Footer: () => {
          const evals = getEvalsForTime(time);
          const number = evals.length;
          return (
            <p>
              {number > 1 ? (
                <Button isColor="primary" onClick={this.openModal(evals)}>
                  +{number - 1} More
                </Button>
              ) : (
                ''
              )}
            </p>
          );
        },
      });

      return {
        Header: day.format('DD-MMM-YY'),
        columns: [makeTimeColumn('AM'), makeTimeColumn('PM')],
      };
    });

    return [
      {
        Header: ' ',
        id: `${animal.aNumber}-titles-ALL`,
        accessor: (keyname: string) => (
          <p className="is-capitalized title is-size-7">{keyname.replace(/([a-z])([A-Z])/g, '$1 $2')}</p>
        ),
      },
      ...weekColumns,
    ];
  };

  handleDateChange = (startDate: Moment, endDate: Moment) => {
    this.setState({
      startDate,
      endDate,
    });
  };

  render() {
    const { animal, isLoading, isModalOpen, modalEvals } = this.state;
    const today = moment();
    const startOfWeek = today.startOf('week');
    const endOfWeek = today.endOf('week');

    if (!animal || isLoading) {
      return (
        <Container>
          <Box>
            <LoadingSpinner />
          </Box>
        </Container>
      );
    }

    const columns = this.getColumns(animal);
    const data = ['initial', 'appetiteDry', 'appetiteWet', 'drinking', 'urine', 'stool', 'vomit', 'sneeze'];
    return (
      <>
        <Container>
          <Box>
            <Title>Evaluation Data - {animal.name}</Title>
            <Subtitle>
              {startOfWeek.format('dddd MMMM DD, YYYY')} to {endOfWeek.format('dddd MMMM DD, YYYY')}
            </Subtitle>
            <DateRangeForm handleSubmit={this.handleDateChange} />
            <Section>
              <ReactTable
                data={data}
                columns={columns}
                className="-striped -highlight"
                showPagination={false}
                defaultPageSize={data.length}
                sortable={false}
                multiSort={false}
                resizable={true}
                filterable={false}
              />
            </Section>
          </Box>
        </Container>
        <Modal header="More Evaluations" isOpen={isModalOpen} handleClose={this.closeModal}>
          <ReactTable
            data={data}
            columns={[
              {
                Header: ' ',
                id: `${animal.aNumber}-titles-ALL`,
                accessor: (keyname: string) => (
                  <p className="is-capitalized title is-size-7">{keyname.replace(/([a-z])([A-Z])/g, '$1 $2')}</p>
                ),
              },
              ...modalEvals.map(evaluation => ({
                Header: () => `${moment(evaluation.timestamp).format('hh:mm A')} (${evaluation.time})`,
                id: evaluation._id,
                className: `column-${evaluation.time}`,
                accessor: (keyname: any) => evaluation[keyname as keyof Evaluation],
              })),
            ]}
            className="-striped -highlight"
            showPagination={false}
            defaultPageSize={data.length}
            sortable={false}
            multiSort={false}
            resizable={true}
            filterable={false}
          />
        </Modal>
      </>
    );
  }
}

export default EvaluationSingleAnimalStatistics;
